import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../ui/Modal';
import { fromDraft, toDraft } from './runConfigForm';

const label = { display: 'block', marginBottom: '0.375rem' };

/**
 * Add or edit one run configuration. Saving writes the whole list to
 * `<repo>/.apiboo/launch.json` (the parent does that); this modal only
 * builds the configuration and reports field errors.
 */
export default function RunConfigEditorModal({ initial, takenNames = [], onSubmit, onClose }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => toDraft(initial));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const res = fromDraft(draft, takenNames);
    if (res.error) {
      setError(t(`projects.run.editor.errors.${res.error}`));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(res.config);
    } catch (err) {
      setError(t('projects.run.saveFailed', { error: String(err?.message || err) }));
      setBusy(false);
    }
  };

  const field = (id, key, extra = {}) => (
    <div>
      <label className="modal-logo-label" htmlFor={`run-${id}`} style={label}>
        {t(`projects.run.editor.${id}`)}
      </label>
      <input
        id={`run-${id}`}
        type="text"
        className="modal-input modal-input-flat"
        value={draft[key]}
        onChange={set(key)}
        autoComplete="off"
        spellCheck={false}
        {...extra}
      />
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} title={initial?.name ? t('projects.run.editor.editTitle') : t('projects.run.editor.addTitle')}>
      <form className="flex flex-col" style={{ gap: '0.875rem' }} onSubmit={submit} data-testid="run-config-editor">
        {field('name', 'name', { autoFocus: true, placeholder: 'web' })}
        {field('command', 'runtimeExecutable', { placeholder: 'npm' })}
        {field('args', 'args', { placeholder: 'run dev' })}
        <div className="flex" style={{ gap: '0.75rem' }}>
          <div style={{ flex: '0 0 8rem' }}>{field('port', 'port', { inputMode: 'numeric', placeholder: '5173' })}</div>
          <div style={{ flex: 1 }}>{field('cwd', 'cwd', { placeholder: '.' })}</div>
        </div>
        <div>
          <label className="modal-logo-label" htmlFor="run-env" style={label}>
            {t('projects.run.editor.env')}
          </label>
          <textarea
            id="run-env"
            className="modal-input modal-input-flat"
            style={{ resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
            rows={3}
            placeholder="NODE_ENV=development"
            value={draft.env}
            onChange={set('env')}
            spellCheck={false}
          />
          <p className="pj-result-meta" style={{ marginTop: '0.375rem' }}>
            {t('projects.run.editor.envHint')}
          </p>
        </div>
        {error && (
          <div className="graph-error" role="alert">
            {error}
          </div>
        )}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
